import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ChevronUp } from 'lucide-react';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { useLanguage } from './contexts/LanguageContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthCallback } from './components/AuthCallback';
import { LoginPage } from './pages/LoginPage';
import { LibraryPage } from './pages/LibraryPage';
import { AboutPage } from './pages/AboutPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import ArchivePage from './pages/ArchivePage';
import GrowAlongPage from './pages/GrowAlongPage';
import ShareTargetPage from './pages/ShareTargetPage';
import { ErrorBoundary } from './components/ErrorBoundary';

// Scroll to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  
  if (location.hash && (location.hash.includes('session_id=') || location.hash.includes('error='))) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/g" element={<GrowAlongPage />} />
      <Route path="/g/plant" element={<GrowAlongPage />} />
      <Route path="/g/plant/:slug" element={<GrowAlongPage />} />
      <Route path="/g/:slug" element={<GrowAlongPage />} />
      <Route path="/share-target" element={<ShareTargetPage />} />
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <LibraryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/archived"
        element={
          <ProtectedRoute>
            <ArchivePage />
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<LoginPage />} />
      <Route path="*" element={<Navigate to="/library" replace />} />
    </Routes>
  );
}

function GlobalFooter() {
  const location = useLocation();
  const { t } = useLanguage();
  const isLoginPage = location.pathname === '/' || location.pathname === '/login';
  if (isLoginPage) return null;

  return (
    <footer role="contentinfo" className="flex flex-col items-center gap-3 px-4 py-4" style={{ backgroundColor: 'hsl(82, 55%, 93%)', borderTop: '1px solid hsl(143, 28%, 88%)' }}>
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-medium shadow-sm transition-all hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500/50"
        style={{ backgroundColor: 'hsl(143, 28%, 40%)', color: 'white' }}
        data-testid="back-to-top-btn"
        title={t('backToTop')}
        aria-label={t('backToTop')}
      >
        <ChevronUp className="w-4 h-4" aria-hidden="true" />
        {t('backToTop')}
      </button>
      <a href="https://www.buymeacoffee.com/Andreakim" target="_blank" rel="noopener noreferrer" data-testid="buy-me-coffee-link">
        <img src="https://cdn.buymeacoffee.com/buttons/v2/default-green.png" alt="Buy Me A Coffee" style={{ height: '40px', width: '145px' }} />
      </a>
      <div className="flex items-center gap-4">
        <Link to="/about" className="text-sm underline hover:opacity-80 transition-opacity" style={{ color: 'hsl(125, 15%, 32%)' }} data-testid="global-about-link">
          {t('aboutAndInstructions')}
        </Link>
        <span className="text-sm" aria-hidden="true" style={{ color: 'hsl(125, 12%, 50%)' }}>|</span>
        <Link
          to="/terms"
          className="text-sm underline hover:opacity-80 transition-opacity" style={{ color: 'hsl(125, 15%, 32%)' }} data-testid="global-tos-link"
        >
          {t('termsOfService')}
        </Link>
        <span className="text-sm" aria-hidden="true" style={{ color: 'hsl(125, 12%, 50%)' }}>|</span>
        <Link
          to="/privacy"
          className="text-sm underline hover:opacity-80 transition-opacity" style={{ color: 'hsl(125, 15%, 32%)' }} data-testid="global-privacy-policy-link"
        >
          {t('privacyPolicy')}
        </Link>
      </div>
    </footer>
  );
}

function ThemedToaster() {
  const { effectiveTheme } = useTheme();
  // Toast position preference — top-center (default) vs bottom-center.
  // Stored in localStorage; re-read on every mount and every time the
  // Settings modal writes it (via a `storage`-scope custom event).
  const [position, setPosition] = React.useState(() => {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem('plant_toast_position') : null;
    return v === 'bottom-center' ? 'bottom-center' : 'top-center';
  });
  React.useEffect(() => {
    const onChange = () => {
      const v = localStorage.getItem('plant_toast_position');
      setPosition(v === 'bottom-center' ? 'bottom-center' : 'top-center');
    };
    // Both cross-tab (`storage`) and same-tab (`plant-toast-position`)
    // updates are honoured so the Settings toggle takes effect live.
    window.addEventListener('storage', onChange);
    window.addEventListener('plant-toast-position', onChange);
    return () => {
      window.removeEventListener('storage', onChange);
      window.removeEventListener('plant-toast-position', onChange);
    };
  }, []);
  return <Toaster position={position} richColors theme={effectiveTheme} />;
}

function App() {
  return (
    <div className="App flex flex-col min-h-screen">
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <div className="flex-1">
              <ErrorBoundary>
                <AppRouter />
              </ErrorBoundary>
            </div>
            <GlobalFooter />
            <ThemedToaster />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
